import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut } from '@aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { telemetryClient } from '../services/telemetryClient';
import { getTrialStatus, TrialStatus } from '../actions/subscription/getTrialStatus';
import { deriveCanUseTools } from './canUseTools';

interface DecodedIdToken {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  "custom:paid"?: string;
  "custom:subscriptionTier"?: string;
}

interface User {
  email: string;
  name: string;
  isApproved: boolean;
  subscriptionTier?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  /**
   * Legacy field — true only for paid (custom:paid==="true") users. Use
   * `canUseTools` for new code; this is kept so older call sites still compile
   * during the rollout.
   */
  isApproved: boolean;
  /**
   * True when the user can dispatch jobs: paid OR within an active 14-day
   * trial AND under the $15 cap. Derived from trialState.
   */
  canUseTools: boolean;
  trialState: TrialStatus | null;
  refreshTrialState: () => Promise<TrialStatus | null>;
  /** Frontend-driven gate-modal state. Tools open it when access is blocked. */
  isGateModalOpen: boolean;
  setGateModalOpen: (open: boolean) => void;
  isLoading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshStatus: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  isProcessingModalOpen: boolean;
  setProcessingModalOpen: (isOpen: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const TRIAL_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trialState, setTrialState] = useState<TrialStatus | null>(null);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState<boolean>(false);
  const [isGateModalOpen, setIsGateModalOpen] = useState<boolean>(false);
  const trialRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshTrialState = useCallback(async (): Promise<TrialStatus | null> => {
    try {
      const status = await getTrialStatus();
      setTrialState(status);
      return status;
    } catch (err) {
      // Soft-fail: a transient backend hiccup shouldn't kick the user out.
      // canUseTools falls back to the JWT's custom:paid claim so paid users
      // still work even if /subscription/trial-status briefly errors.
      console.warn('Failed to fetch trial status, falling back to JWT claim', err);
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setTrialState(null);
      telemetryClient.setUserId(null);
      setError(null);
      setIsProcessingModalOpen(false);
      setIsGateModalOpen(false);
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError(err.message || 'Failed to sign out');
    }
  }, []);

  const refreshStatus = useCallback(async (): Promise<boolean> => {
    try {
      setError(null);
      await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        const decoded: DecodedIdToken = jwtDecode(idToken);

        let fullName = "User";
        if (decoded.name) {
          fullName = decoded.name;
        } else if (decoded.given_name) {
          const firstName = decoded.given_name;
          const lastName = decoded.family_name || "";
          fullName = lastName ? `${firstName} ${lastName}` : firstName;
        } else if (decoded.family_name) {
          fullName = decoded.family_name;
        }

        const isApproved = decoded["custom:paid"] === "true";
        const subscriptionTier = decoded["custom:subscriptionTier"] || undefined;
        const userData: User = {
          email: decoded.email || '',
          name: fullName,
          isApproved,
          subscriptionTier,
        };

        setUser(userData);
        telemetryClient.setUserId(decoded.sub ?? null);

        // Pull trial state in parallel (best-effort, see refreshTrialState).
        await refreshTrialState();

        return isApproved;
      } else {
        setUser(null);
        setTrialState(null);
        telemetryClient.setUserId(null);
        return false;
      }
    } catch (err: any) {
      console.info("User not authenticated or session expired:", err);
      setUser(null);
      setTrialState(null);
      telemetryClient.setUserId(null);

      if (err.name === 'UserUnAuthenticatedException' && err.message?.includes('needs to be authenticated')) {
        setError('Your account is not confirmed. Please check your email for the verification code.');
        throw err;
      } else {
        setError(null);
        return false;
      }
    }
  }, [refreshTrialState]);

  // Initial auth check
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      try {
        await refreshStatus();
      } catch (error) {
        console.info("Initial auth check failed (expected for unauthenticated users):", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [refreshStatus]);

  // Periodic trial-state refresh while authenticated.
  useEffect(() => {
    if (!user) {
      if (trialRefreshTimerRef.current) {
        clearInterval(trialRefreshTimerRef.current);
        trialRefreshTimerRef.current = null;
      }
      return;
    }

    trialRefreshTimerRef.current = setInterval(() => {
      refreshTrialState();
    }, TRIAL_REFRESH_INTERVAL_MS);

    return () => {
      if (trialRefreshTimerRef.current) {
        clearInterval(trialRefreshTimerRef.current);
        trialRefreshTimerRef.current = null;
      }
    };
  }, [user, refreshTrialState]);

  const canUseTools = deriveCanUseTools(trialState, user);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isApproved: user?.isApproved || false,
    canUseTools,
    trialState,
    refreshTrialState,
    isGateModalOpen,
    setGateModalOpen: setIsGateModalOpen,
    isLoading,
    error,
    signOut,
    refreshStatus,
    clearError,
    setUser,
    isProcessingModalOpen,
    setProcessingModalOpen: setIsProcessingModalOpen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
